import React, { useState } from "react";
import Button from "@mui/material/Button";
import { useTranslation } from "react-i18next";
import { PageContainer } from "@outpave/ds";
import AccountType from "enums/AccountType";
import useGetBankAccounts from "hooks/useGetBankAccounts";
import useNavBreadcrumbs from "hooks/useNavBreadcrumbs";
import { Stack } from "components/layout/reusableLayouts";
import AccountFilterButton, {
  ALL_ACCOUNTS_ID,
} from "components/admin/transactions/AccountFilterButton";
import AnalyticsFilterBar from "components/admin/analytics/AnalyticsFilterBar";
import CashFlowPanel from "components/admin/analytics/CashFlowPanel";
import FiltersDrawer from "components/admin/analytics/FiltersDrawer";
import SpendBreakdownPanel from "components/admin/analytics/SpendBreakdownPanel";
import ReportsPanel from "components/admin/analytics/ReportsPanel";
import {
  AnalyticsFilters,
  DEFAULT_ANALYTICS_FILTERS,
} from "components/admin/analytics/analyticsFilters";

const ACCOUNT_TYPES = [
  AccountType.FINANCIAL_ACCOUNT,
  AccountType.PLAID_EXTERNAL_CARD_ACCOUNT,
];

const Analytics: React.FC = () => {
  const { t } = useTranslation();
  const breadcrumbs = useNavBreadcrumbs(t("Analytics"));
  const [filters, setFilters] = useState<AnalyticsFilters>(
    DEFAULT_ANALYTICS_FILTERS,
  );
  const [drawerOpen, setDrawerOpen] = useState(false);

  const bankAccounts = useGetBankAccounts({ accountTypes: ACCOUNT_TYPES });

  return (
    <PageContainer
      title={t("Analytics")}
      breadcrumbs={breadcrumbs}
      wrapped={false}
      secondaryAction={
        <AccountFilterButton
          accounts={bankAccounts.allAccounts}
          value={filters.accountId ?? ALL_ACCOUNTS_ID}
          onChange={(accountId) =>
            setFilters({
              ...filters,
              accountId: accountId === ALL_ACCOUNTS_ID ? null : accountId,
            })
          }
        />
      }
      primaryAction={
        <Button onClick={() => setDrawerOpen(true)}>{t("Add Filters")}</Button>
      }
      filters={
        <AnalyticsFilterBar
          filters={filters}
          onChange={setFilters}
          onEditFilters={() => setDrawerOpen(true)}
        />
      }
    >
      <Stack gap="var(--space-4)" sx={{ paddingBottom: "var(--space-7)" }}>
        <CashFlowPanel
          bankAccounts={bankAccounts}
          accountId={filters.accountId}
          timeFrame={filters.timeFrame}
        />
        <SpendBreakdownPanel filters={filters} />
        <ReportsPanel />
      </Stack>

      <FiltersDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        filters={filters}
        onChange={setFilters}
      />
    </PageContainer>
  );
};

export default Analytics;
